import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

import Header from './Header';
import Footer from './Footer';

import Main from '../routes/Main';
import Artist from '../routes/Artist';
import ArtistDetail from '../routes/ArtistDetail';
import Press from '../routes/Press';
import PressDetail from '../routes/PressDetail';
import History from '../routes/History';
import Contact from '../routes/Contact';
import NotFound from '../routes/NotFound';

import { VALID_PATHS, VALID_PATTERNS } from '../constants/routes';
import { BREAK_POINTS } from '../constants';

const RouterWrapper = styled.div`
  padding-top: 200px;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    padding-top: 160px;
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    padding-top: 100px;
  }
`;

const pageTransition = {
  web: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
  mobile: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  },
};

export default function Router() {
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= BREAK_POINTS.MOBILE);

  const is404 = React.useMemo(() => {
    const path = location.pathname;
    if (VALID_PATHS.includes(path)) return false;
    if (VALID_PATTERNS.some(pattern => pattern.test(path))) return false;
    return true;
  }, [location.pathname]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= BREAK_POINTS.MOBILE);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentVariants = isMobile ? pageTransition.mobile : pageTransition.web;
  return (
    <AnimatePresence mode="wait">
      <RouterWrapper>
        {!is404 && <Header />}

        <Routes>
          <Route 
            path="/" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <Main />
              </motion.div>
            } 
          />
          <Route 
            path="/artist" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <Artist />
              </motion.div>
            } 
          />
          <Route 
            path="/artist/:id" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <ArtistDetail />
              </motion.div>
            } 
          />
          <Route 
            path="/press/page/:page" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <Press />
              </motion.div>
            } 
          />
          <Route 
            path="/press/detail/:id" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <PressDetail />
              </motion.div>
            } 
          />
          <Route 
            path="/company" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <History />
              </motion.div>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <motion.div key={location.pathname} {...currentVariants}>
                <Contact />
              </motion.div>
            } 
          />
          <Route path="*" element={<NotFound />} />
        </Routes>

        {!is404 && <Footer />}
      </RouterWrapper>
    </AnimatePresence>
  );
};
